/**
 * Personalization Engine
 *
 * Analyzes user's tasting notes, saved cigars, and smoke log to build
 * a dynamic taste profile and provide personalized recommendations.
 */

import { TastingNote } from '../types';
import { Cigar, CIGARS, getSimilarCigars } from './cigars';

// Types for personalization
export interface TasteProfile {
  // Strength preferences (weighted by recency and rating)
  strengthPreference: {
    mild: number;
    'mild-medium': number;
    medium: number;
    'medium-full': number;
    full: number;
  };

  // Flavor affinities (0-1 scale)
  flavorAffinities: Record<string, number>;

  // Top flavors by frequency and rating
  topFlavors: string[];

  // Preferred body
  bodyPreference: 'light' | 'medium' | 'full' | null;

  // Favorite origins
  preferredOrigins: string[];

  // Price range tendency
  pricePreference: 'budget' | 'moderate' | 'premium' | 'luxury' | null;

  // Favorite brands
  favoriteBrands: string[];

  // Smoking patterns
  averageSmokeDuration: number | null;
  preferredOccasions: string[];
  preferredPairings: string[];

  // Experience level estimate
  estimatedExperience: 'beginner' | 'intermediate' | 'aficionado';

  // Profile confidence (0-1) - higher with more data
  confidence: number;

  // Data points count
  noteCount: number;
  savedCount: number;
  smokeLogCount: number;
}

export interface SmokeLogEntry {
  id: string;
  cigarId?: string;        // Reference to CIGARS array
  brand: string;
  line?: string;
  smokeDate: string;
  duration?: number;       // minutes
  rating?: number;         // 1-5
  mood?: string;
  notes?: string;
  location?: string;
  pairing?: string;
  wouldRepeat: boolean;
  bandImageUrl?: string;   // R2 URL for band photo
  bandImageId?: string;    // Unique ID for the band image
  createdAt: string;
}

export interface PersonalizedRecommendation {
  cigar: Cigar;
  matchScore: number;      // 0-100
  matchReasons: string[];
  noveltyScore: number;    // How different from what they usually smoke
}

// localStorage keys
const TASTE_PROFILE_KEY = 'ring_ash_taste_profile';
const SMOKE_LOG_KEY = 'ring_ash_smoke_log';

// Get empty taste profile
const getEmptyProfile = (): TasteProfile => ({
  strengthPreference: {
    mild: 0,
    'mild-medium': 0,
    medium: 0,
    'medium-full': 0,
    full: 0,
  },
  flavorAffinities: {},
  topFlavors: [],
  bodyPreference: null,
  preferredOrigins: [],
  pricePreference: null,
  favoriteBrands: [],
  averageSmokeDuration: null,
  preferredOccasions: [],
  preferredPairings: [],
  estimatedExperience: 'beginner',
  confidence: 0,
  noteCount: 0,
  savedCount: 0,
  smokeLogCount: 0,
});

/**
 * Build taste profile from journal entries and saved cigars
 */
export const buildTasteProfile = (
  notes: TastingNote[],
  savedCigarIds: string[],
  smokeLog: SmokeLogEntry[]
): TasteProfile => {
  const profile = getEmptyProfile();

  // Get saved cigars from database
  const savedCigars = CIGARS.filter(c => savedCigarIds.includes(c.id));

  profile.noteCount = notes.length;
  profile.savedCount = savedCigarIds.length;
  profile.smokeLogCount = smokeLog.length;

  // Calculate confidence based on data points
  const totalDataPoints = notes.length * 3 + savedCigars.length + smokeLog.length * 2;
  profile.confidence = Math.min(1, totalDataPoints / 50); // Max confidence at 50 data points

  if (totalDataPoints === 0) {
    return profile;
  }

  // Analyze strength preferences from notes
  const strengthCounts: Record<string, { count: number; totalRating: number }> = {};
  notes.forEach(note => {
    if (note.overallStrength) {
      if (!strengthCounts[note.overallStrength]) {
        strengthCounts[note.overallStrength] = { count: 0, totalRating: 0 };
      }
      strengthCounts[note.overallStrength].count++;
      strengthCounts[note.overallStrength].totalRating += note.overallScore || 3;
    }
  });

  // Add saved cigars to strength analysis
  savedCigars.forEach(cigar => {
    if (!strengthCounts[cigar.strength]) {
      strengthCounts[cigar.strength] = { count: 0, totalRating: 0 };
    }
    strengthCounts[cigar.strength].count++;
    strengthCounts[cigar.strength].totalRating += 4; // Assume saved = liked
  });

  // Normalize strength preferences
  const maxStrengthScore = Math.max(
    ...Object.values(strengthCounts).map(s => s.count * (s.totalRating / s.count || 1))
  );
  if (maxStrengthScore > 0) {
    Object.entries(strengthCounts).forEach(([strength, data]) => {
      const score = data.count * (data.totalRating / data.count || 1);
      profile.strengthPreference[strength as keyof typeof profile.strengthPreference] =
        score / maxStrengthScore;
    });
  }

  // Analyze flavor preferences
  const flavorCounts: Record<string, { count: number; weight: number }> = {};

  // From journal notes
  notes.forEach(note => {
    const weight = (note.overallScore || 3) / 5;
    note.flavors.forEach(flavor => {
      if (!flavorCounts[flavor]) {
        flavorCounts[flavor] = { count: 0, weight: 0 };
      }
      flavorCounts[flavor].count++;
      flavorCounts[flavor].weight += weight;
    });
  });

  // From saved cigars
  savedCigars.forEach(cigar => {
    [...cigar.primaryFlavors, ...cigar.flavorNotes].forEach(flavor => {
      const normalizedFlavor = flavor.toLowerCase();
      if (!flavorCounts[normalizedFlavor]) {
        flavorCounts[normalizedFlavor] = { count: 0, weight: 0 };
      }
      flavorCounts[normalizedFlavor].count++;
      flavorCounts[normalizedFlavor].weight += 0.8;
    });
  });

  // Calculate flavor affinities
  const maxFlavorWeight = Math.max(...Object.values(flavorCounts).map(f => f.weight));
  if (maxFlavorWeight > 0) {
    Object.entries(flavorCounts).forEach(([flavor, data]) => {
      profile.flavorAffinities[flavor] = data.weight / maxFlavorWeight;
    });
  }

  // Get top flavors
  profile.topFlavors = Object.entries(profile.flavorAffinities)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([flavor]) => flavor);

  // Analyze brand preferences
  const brandCounts: Record<string, number> = {};
  notes.forEach(note => {
    brandCounts[note.brand] = (brandCounts[note.brand] || 0) + 1;
  });
  savedCigars.forEach(cigar => {
    brandCounts[cigar.brand] = (brandCounts[cigar.brand] || 0) + 1;
  });
  smokeLog.forEach(entry => {
    brandCounts[entry.brand] = (brandCounts[entry.brand] || 0) + 1;
  });

  profile.favoriteBrands = Object.entries(brandCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([brand]) => brand);

  // Analyze origins
  const originCounts: Record<string, number> = {};
  savedCigars.forEach(cigar => {
    originCounts[cigar.origin] = (originCounts[cigar.origin] || 0) + 1;
  });
  notes.forEach(note => {
    if (note.origin) {
      originCounts[note.origin] = (originCounts[note.origin] || 0) + 1;
    }
  });

  profile.preferredOrigins = Object.entries(originCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([origin]) => origin);

  // Analyze pairings
  const pairingCounts: Record<string, number> = {};
  notes.forEach(note => {
    if (note.pairingType && note.pairingType !== 'none') {
      pairingCounts[note.pairingType] = (pairingCounts[note.pairingType] || 0) + 1;
    }
  });
  smokeLog.forEach(entry => {
    if (entry.pairing) {
      pairingCounts[entry.pairing] = (pairingCounts[entry.pairing] || 0) + 1;
    }
  });

  profile.preferredPairings = Object.entries(pairingCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3)
    .map(([pairing]) => pairing);

  // Calculate average smoke duration
  const durations = [
    ...notes.filter(n => n.smokeDuration).map(n => n.smokeDuration!),
    ...smokeLog.filter(e => e.duration).map(e => e.duration!),
  ];
  if (durations.length > 0) {
    profile.averageSmokeDuration = Math.round(
      durations.reduce((a, b) => a + b, 0) / durations.length
    );
  }

  // Estimate experience level
  const totalSmokes = notes.length + smokeLog.length;
  const avgStrength = Object.entries(profile.strengthPreference)
    .reduce((sum, [str, val]) => {
      const strengthIndex = ['mild', 'mild-medium', 'medium', 'medium-full', 'full'].indexOf(str);
      return sum + strengthIndex * val;
    }, 0);

  if (totalSmokes >= 50 || avgStrength > 3) {
    profile.estimatedExperience = 'aficionado';
  } else if (totalSmokes >= 15 || avgStrength > 2) {
    profile.estimatedExperience = 'intermediate';
  } else {
    profile.estimatedExperience = 'beginner';
  }

  // Determine price preference from saved cigars
  const priceCounts: Record<string, number> = {};
  savedCigars.forEach(cigar => {
    priceCounts[cigar.priceRange] = (priceCounts[cigar.priceRange] || 0) + 1;
  });

  if (Object.keys(priceCounts).length > 0) {
    profile.pricePreference = Object.entries(priceCounts)
      .sort(([, a], [, b]) => b - a)[0][0] as typeof profile.pricePreference;
  }

  // Determine body preference from saved cigars
  const bodyCounts: Record<string, number> = {};
  savedCigars.forEach(cigar => {
    bodyCounts[cigar.body] = (bodyCounts[cigar.body] || 0) + 1;
  });

  if (Object.keys(bodyCounts).length > 0) {
    profile.bodyPreference = Object.entries(bodyCounts)
      .sort(([, a], [, b]) => b - a)[0][0] as typeof profile.bodyPreference;
  }

  return profile;
};

/**
 * Get personalized cigar recommendations based on taste profile
 */
export const getPersonalizedRecommendations = (
  profile: TasteProfile,
  savedCigarIds: string[],
  smokedCigarIds: string[],
  limit: number = 10
): PersonalizedRecommendation[] => {
  // Skip cigars already saved or smoked
  const excludeIds = new Set([...savedCigarIds, ...smokedCigarIds]);

  return CIGARS
    .filter(cigar => !excludeIds.has(cigar.id))
    .map(cigar => {
      let matchScore = 0;
      const matchReasons: string[] = [];

      // Strength match (0-30 points)
      const strengthScore = profile.strengthPreference[cigar.strength] || 0;
      const strengthPoints = Math.round(strengthScore * 30);
      matchScore += strengthPoints;
      if (strengthPoints >= 20) {
        matchReasons.push(`Matches your preferred ${cigar.strength} strength`);
      }

      // Flavor match (0-40 points)
      const cigarFlavors = [...cigar.primaryFlavors, ...cigar.flavorNotes].map(f => f.toLowerCase());
      let flavorScore = 0;
      const matchedFlavors: string[] = [];

      cigarFlavors.forEach(flavor => {
        if (profile.flavorAffinities[flavor]) {
          flavorScore += profile.flavorAffinities[flavor];
          if (profile.topFlavors.includes(flavor)) {
            matchedFlavors.push(flavor);
          }
        }
      });

      const normalizedFlavorScore = Math.min(1, flavorScore / 3);
      const flavorPoints = Math.round(normalizedFlavorScore * 40);
      matchScore += flavorPoints;

      if (matchedFlavors.length > 0) {
        matchReasons.push(`Features ${matchedFlavors.slice(0, 2).join(' and ')} flavors you love`);
      }

      // Origin match (0-10 points)
      if (profile.preferredOrigins.includes(cigar.origin)) {
        matchScore += 10;
        matchReasons.push(`From ${cigar.origin}, one of your favorite origins`);
      }

      // Brand match (0-10 points)
      if (profile.favoriteBrands.includes(cigar.brand)) {
        matchScore += 10;
        matchReasons.push(`By ${cigar.brand}, a brand you enjoy`);
      }

      // Time commitment match (0-10 points)
      if (profile.averageSmokeDuration) {
        const timeDiff = Math.abs(cigar.timeCommitment - profile.averageSmokeDuration);
        if (timeDiff <= 15) {
          matchScore += 10;
          matchReasons.push('Fits your typical smoking time');
        } else if (timeDiff <= 30) {
          matchScore += 5;
        }
      }

      // Calculate novelty score (how different from usual)
      let noveltyScore = 50; // Start at neutral

      // Different strength = more novel
      if (profile.strengthPreference[cigar.strength] < 0.3) {
        noveltyScore += 20;
      }

      // Unique flavors = more novel
      const uniqueFlavors = cigarFlavors.filter(f => !profile.flavorAffinities[f]);
      noveltyScore += Math.min(30, uniqueFlavors.length * 10);

      return {
        cigar,
        matchScore: Math.min(100, matchScore),
        matchReasons: matchReasons.slice(0, 3),
        noveltyScore: Math.min(100, noveltyScore),
      };
    })
    .sort((a, b) => b.matchScore - a.matchScore)
    .slice(0, limit);
};

/**
 * Get personalized "Today's Smoke" based on profile
 */
export const getPersonalizedTodaysSmoke = (
  profile: TasteProfile,
  savedCigarIds: string[],
  recentlySmokedIds: string[] // Last 7 days
): Cigar => {
  // If no profile data, fall back to standard Today's Smoke
  if (profile.confidence < 0.1) {
    const dayOfYear = Math.floor(
      (Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000
    );
    return CIGARS[dayOfYear % CIGARS.length];
  }

  // Get recommendations excluding recently smoked
  const recommendations = getPersonalizedRecommendations(
    profile,
    savedCigarIds,
    recentlySmokedIds,
    5
  );

  // Add some daily variety using day of year
  const dayOfYear = Math.floor(
    (Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000
  );

  if (recommendations.length > 0) {
    // Rotate through top recommendations based on day
    return recommendations[dayOfYear % recommendations.length].cigar;
  }

  // Fallback
  return CIGARS[dayOfYear % CIGARS.length];
};

/**
 * Get "If You Like X, Try Y" suggestions
 */
export const getIfYouLikeTryThis = (
  likedCigar: Cigar,
  _profile: TasteProfile, // Reserved for future use
  savedCigarIds: string[]
): { cigar: Cigar; reason: string }[] => {
  const similar = getSimilarCigars(likedCigar, 6);

  return similar
    .filter(c => !savedCigarIds.includes(c.id))
    .slice(0, 3)
    .map(cigar => {
      let reason = '';

      // Find the key similarity
      if (cigar.strength === likedCigar.strength) {
        reason = `Same ${cigar.strength} strength`;
      } else {
        const sharedFlavors = cigar.primaryFlavors.filter(f =>
          likedCigar.primaryFlavors.includes(f)
        );
        if (sharedFlavors.length > 0) {
          reason = `Shares ${sharedFlavors[0]} notes`;
        } else if (cigar.origin === likedCigar.origin) {
          reason = `Also from ${cigar.origin}`;
        } else if (cigar.wrapper.includes(likedCigar.wrapper.split(' ')[0])) {
          reason = `Similar wrapper style`;
        } else {
          reason = 'Complementary flavor profile';
        }
      }

      return { cigar, reason };
    });
};

// Smoke log management
export const getSmokeLog = (): SmokeLogEntry[] => {
  try {
    return JSON.parse(localStorage.getItem(SMOKE_LOG_KEY) || '[]');
  } catch {
    return [];
  }
};

export const saveSmokeLog = (log: SmokeLogEntry[]) => {
  localStorage.setItem(SMOKE_LOG_KEY, JSON.stringify(log));
};

export const addSmokeLogEntry = (entry: Omit<SmokeLogEntry, 'id' | 'createdAt'>): SmokeLogEntry => {
  const log = getSmokeLog();
  const newEntry: SmokeLogEntry = {
    ...entry,
    id: `smoke-${Date.now()}`,
    createdAt: new Date().toISOString(),
  };
  log.unshift(newEntry);
  saveSmokeLog(log);
  return newEntry;
};

export const deleteSmokeLogEntry = (entryId: string) => {
  const log = getSmokeLog();
  const updated = log.filter(e => e.id !== entryId);
  saveSmokeLog(updated);
};

export const getRecentlySmokedIds = (days: number = 7): string[] => {
  const log = getSmokeLog();
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);

  return log
    .filter(entry => new Date(entry.smokeDate) >= cutoff && entry.cigarId)
    .map(entry => entry.cigarId!);
};

// Taste profile caching
export const getCachedTasteProfile = (): TasteProfile | null => {
  try {
    const cached = localStorage.getItem(TASTE_PROFILE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

export const cacheTasteProfile = (profile: TasteProfile) => {
  localStorage.setItem(TASTE_PROFILE_KEY, JSON.stringify(profile));
};

// Generate profile insights for UI display
export const getProfileInsights = (profile: TasteProfile): string[] => {
  const insights: string[] = [];

  if (profile.confidence < 0.2) {
    insights.push("Add more cigars to your journal to get personalized recommendations");
    return insights;
  }

  // Strength insight
  const topStrength = Object.entries(profile.strengthPreference)
    .sort(([, a], [, b]) => b - a)[0];
  if (topStrength && topStrength[1] > 0.5) {
    insights.push(`You prefer ${topStrength[0]} cigars`);
  }

  // Flavor insights
  if (profile.topFlavors.length >= 2) {
    insights.push(`${profile.topFlavors[0]} and ${profile.topFlavors[1]} are your favorite flavor notes`);
  }

  // Origin insight
  if (profile.preferredOrigins.length > 0) {
    insights.push(`You gravitate toward cigars from ${profile.preferredOrigins[0]}`);
  }

  // Pairing insight
  if (profile.preferredPairings.length > 0) {
    insights.push(`${profile.preferredPairings[0]} is your go-to pairing`);
  }

  // Time insight
  if (profile.averageSmokeDuration) {
    insights.push(`Your average smoke session is ${profile.averageSmokeDuration} minutes`);
  }

  // Experience insight
  if (profile.estimatedExperience === 'aficionado') {
    insights.push("You're an experienced aficionado with refined tastes");
  } else if (profile.estimatedExperience === 'intermediate') {
    insights.push("You're developing a sophisticated palate");
  }

  return insights.slice(0, 4);
};
